export type MonthLexiconEntry = {
  monthIndex: number;
  long: string;
  short: string;
};

export type ParsedTextDateQuery = {
  monthIndex: number;
  day?: number;
  year?: number;
};

export function getDateFormatWithYear(language?: string): string {
  return language && language.startsWith('fr') ? 'D MMMM YYYY' : 'MMMM D, YYYY';
}

export function normalizeForDateSearch(s: string): string {
  return (s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function buildMonthLexicon(language?: string): MonthLexiconEntry[] {
  const locale = language || 'en';
  const longFmt = new Intl.DateTimeFormat(locale, { month: 'long' });
  const shortFmt = new Intl.DateTimeFormat(locale, { month: 'short' });

  return Array.from({ length: 12 }, (_, monthIndex) => {
    const d = new Date(2020, monthIndex, 1);
    const long = normalizeForDateSearch(longFmt.format(d));
    const short = normalizeForDateSearch(shortFmt.format(d));
    return { monthIndex, long, short };
  });
}

export function parseTextDateQuery(
  query: string,
  monthLexicon: MonthLexiconEntry[]
): ParsedTextDateQuery | null {
  const normalized = normalizeForDateSearch(query);
  if (!normalized) return null;

  const tokens = normalized.split(' ').filter(Boolean);
  if (tokens.length === 0) return null;

  let day: number | undefined;
  let year: number | undefined;

  tokens.forEach((tok) => {
    if (/^\d{4}$/.test(tok)) {
      const y = Number(tok);
      if (y >= 1900 && y <= 2100) year = y;
      return;
    }
    if (/^\d{1,2}$/.test(tok)) {
      const d = Number(tok);
      if (d >= 1 && d <= 31) day = d;
    }
  });

  const monthCandidates = tokens.reduce<number[]>((acc, tok) => {
    if (/^\d+$/.test(tok)) return acc;
    if (tok.length < 2) return acc;

    const matches = monthLexicon
      .filter((m) => m.long.startsWith(tok) || m.short.startsWith(tok))
      .map((m) => m.monthIndex);

    acc.push(...matches);
    return acc;
  }, []);

  const uniqueMonths = Array.from(new Set(monthCandidates));
  if (uniqueMonths.length !== 1) return null;

  return {
    monthIndex: uniqueMonths[0],
    day,
    year,
  };
}

export function isDateQuery(query: string, monthLexicon: MonthLexiconEntry[]) {
  return parseTextDateQuery(query, monthLexicon) !== null;
}

export function sanitizePreviewText(text: string): string {
  return (text || '')
    .replace(/[*_~`#|]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function extractSnippet(text: string, query: string): string {
  const cleaned = sanitizePreviewText(text);
  if (!query.trim() || !cleaned) return cleaned;

  const queryLower = query.toLowerCase();
  const cleanedLower = cleaned.toLowerCase();
  const queryWords = queryLower.trim().split(/\s+/);
  const firstWord = queryWords[0];
  const matchIndex = firstWord ? cleanedLower.indexOf(firstWord) : -1;
  if (matchIndex < 0) return cleaned;

  const start = Math.max(0, matchIndex - 40);
  const end = Math.min(cleaned.length, matchIndex + query.length + 80);
  let snippet = cleaned.substring(start, end);
  if (start > 0) snippet = `...${snippet}`;
  if (end < cleaned.length) snippet = `${snippet}...`;
  return snippet;
}

export function getQueryWords(query: string): string[] {
  return query.trim().toLowerCase().split(/\s+/).filter(Boolean);
}

export function buildHighlightRegex(words: string[]): RegExp | null {
  const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = words
    .map(escapeRegExp)
    .sort((a, b) => b.length - a.length)
    .join('|');

  if (!pattern) return null;
  return new RegExp(`(${pattern})`, 'gi');
}
